import {
  ButtonGroup,
  Button,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import './styles.css';
import dayjs from 'dayjs';
import { useAppSelector } from 'store/hook';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import useShiftConfig from 'hooks/useShiftConfig';
import { formatShiftHour } from 'utils/helpers/customerShiftHelpers';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import useAppTheme from 'hooks/useAppTheme';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import { calculateDaysDifference } from 'views/FuelAnalytics/IdleDashboard/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  ignoreYtd?: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  aggregation: string;
  setAggregation: any;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  shiftNumber: number;
  setShiftNumber: any;
  utilizationType?: string;
  setUtilizationType?: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const DateRangeDiv = ({
  ignoreYtd = false,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  aggregation,
  setAggregation,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  utilizationType,
  setUtilizationType,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const { timezone } = useTimeDisplayMode();
  const { shiftOptions } = useShiftConfig();
  const isLoadingFleetSummary = useAppSelector(
    (state) => state.utilizationReducer.loadingStates.isLoadingFleetSummary
  );
  const [aggregationOptions, setAggregationOptions] = useState([
    'Daily',
    'Monthly',
  ]);
  const handleSelect = useCallback(
    (id: string) => {
      setIsSelected(id);

      const today = dayjs();
      let newStartDate = startDate;
      let newEndDate = yesterday;

      switch (id) {
        case 'Today': {
          const startDateVal = new Date().toISOString().slice(0, 10);
          newStartDate = dayjs(startDateVal).toDate();
          newEndDate = dayjs(startDateVal).toDate();
          setAggregation('Daily');
          break;
        }
        case '1D':
          newStartDate = dayjs(yesterday).subtract(1, 'day').toDate();
          newEndDate = yesterday;
          setAggregation('Daily');
          break;
        case '7D':
          newStartDate = dayjs(yesterday).subtract(7, 'days').toDate();
          newEndDate = yesterday;
          setAggregation('Daily');
          break;
        case '14D':
          newStartDate = dayjs(yesterday).subtract(14, 'days').toDate();
          newEndDate = yesterday;
          setAggregation('Daily');
          break;
        case '1M':
          newStartDate = dayjs(yesterday).subtract(1, 'month').toDate();
          newEndDate = yesterday;
          setAggregation('Daily');
          break;
        case '1Y':
          newStartDate = dayjs(yesterday).subtract(1, 'year').toDate();
          newEndDate = yesterday;
          setAggregation('Monthly');
          break;
        case 'YTD':
          newStartDate = dayjs(today).startOf('year').toDate();
          newEndDate = yesterday;
          setAggregation('Monthly');
          break;
        case 'Custom':
          // Do nothing, user will select manually
          break;
        default:
          break;
      }

      // Update the startDate and endDate for non-custom ranges
      if (id !== 'Custom') {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
      }
    },
    [setStartDate, setEndDate, setIsSelected, setAggregation]
  );

  const dateRangeList = useMemo(() => {
    let list = ['Today', '1D', '7D', '14D', '1M', 'Custom'];
    if (ignoreYtd) {
      list = list.filter((item) => item !== 'YTD');
    }
    return list;
  }, [ignoreYtd]);

  const dateRangeLabel = (item: string): string => {
    const map: Record<string, string> = {
      Today: t('date_range_today'),
      '1D': t('date_range_1d'),
      '7D': t('date_range_7d'),
      '14D': t('date_range_14d'),
      '1M': t('date_range_1m'),
      Custom: t('date_range_custom'),
    };
    return map[item] ?? item;
  };

  const aggregationLabel = (item: string): string => {
    const map: Record<string, string> = {
      Daily: t('aggregation_daily'),
      Monthly: t('aggregation_monthly'),
    };
    return map[item] ?? item;
  };

  const rangeIsOverMonth = useMemo(
    () => ['Custom'].includes(isSelected),
    [isSelected]
  );

  useEffect(() => {
    if (!startDate || !endDate) return;
    const days = calculateDaysDifference(startDate, endDate);
    if (days >= 30) {
      setAggregationOptions(['Daily', 'Monthly']);
    } else {
      if (aggregation === 'Monthly') {
        setAggregation('Daily');
      }
      setAggregationOptions(['Daily']);
    }
  }, [rangeIsOverMonth, startDate, endDate]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          margin: '20px 0 0',
          gap: '10px',
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.borderGrey}`,
          padding: '10px',
        }}
      >
        <div
          style={{
            color: enableDarkTheme
              ? ThemePalette.typography.mediumGray
              : '#3d3d3d',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Typography style={{ fontSize: '11px', marginRight: '5px' }}>
            {t('quick_range_10')}
          </Typography>
          <ButtonGroup
            size="small"
            variant="outlined"
            aria-label={t('basic_button_group')}
            sx={{
              border: `1px solid ${palette.primary}`,
              '& .MuiButton-root': {
                borderColor: `${palette.primary} !important`,
              },
            }}
          >
            {dateRangeList.map((item) => {
              return (
                <Button
                  key={item}
                  size="small"
                  disabled={isLoadingFleetSummary}
                  style={{
                    fontSize: ' 11px ',
                    backgroundColor:
                      isSelected === item
                        ? palette.primary
                        : enableDarkTheme
                        ? palette.boxBackground
                        : palette.toolBarBackground,
                    color: isLoadingFleetSummary
                      ? ThemePalette.typography.grey
                      : isSelected === item
                      ? palette.white
                      : enableDarkTheme
                      ? _palette.dark.textPrimary
                      : _palette.light.textPrimary,
                  }}
                  onClick={() => {
                    handleSelect(item);
                  }}
                >
                  {dateRangeLabel(item)}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
        {isSelected === 'Custom' && (
          <div
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.mediumGray
                : '#3d3d3d',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {aggregation === 'Monthly' ? (
              <CustomDateRangePicker
                startDate={startDate}
                endDate={endDate}
                maxDate={dayjs(yesterday)}
                disabled={isSelected !== 'Custom' || isLoadingFleetSummary}
                onFinalDateChange={handleDateChange}
                views={['month', 'year']}
                format="MMM-YYYY"
                autoOpenEnabled={false}
                // className={
                //   enableDarkTheme
                //     ? 'custom-datepicker-prod'
                //     : 'custom-datepicker-prod-light-theme'
                // }
                // filterDate={(date) => {
                //   const diff = dayjs(date).diff(dayjs(startDate), 'month');
                //   return diff <= 12;
                // }}
              />
            ) : (
              <CustomDateRangePicker
                startDate={startDate}
                endDate={endDate}
                maxDate={dayjs(yesterday)}
                disabled={isSelected !== 'Custom' || isLoadingFleetSummary}
                onFinalDateChange={handleDateChange}
                autoOpenEnabled={false}
                // filterDate={(date) => {
                //   const diff = dayjs(date).diff(dayjs(startDate), 'day');
                //   return diff <= 365;
                // }} // disabling user to select more than 1 year
              />
            )}
            <div style={{ width: '64px' }}>
              <Button
                onClick={() => {
                  handleFetchCustomDateRangeData({ aggregation });
                }}
                disabled={
                  isLoadingFleetSummary ||
                  isSelected !== 'Custom' ||
                  !startDate ||
                  !endDate
                }
                sx={{
                  fontSize: '11px',
                  backgroundColor: palette.primary,
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  border: `1px solid ${palette.primary}`,
                  '&:hover': {
                    backgroundColor: palette.primaryDark,
                    color: '#fff',
                  },
                }}
              >
                {t('apply')}
              </Button>
            </div>
          </div>
        )}
        <Select
          labelId="utilzation-type-label"
          value={utilizationType}
          disabled={isLoadingFleetSummary}
          displayEmpty
          onChange={(event) => {
            setUtilizationType(event.target.value);
          }}
          sx={{
            height: '30px',
            fontSize: '11px',
            backgroundColor: enableDarkTheme
              ? palette.cartesianGrid
              : palette.menuOptions,
            color: enableDarkTheme
              ? _palette.dark.textPrimary
              : _palette.light.textPrimary,
            '& .MuiSelect-icon': {
              color: palette.primary,
            },
            '& fieldset': {
              borderColor: palette.borderGrey,
            },
            '&.MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: palette.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: palette.primary,
              },
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: enableDarkTheme
                  ? _palette.dark.menuOptions
                  : _palette.light.menuOptions,
                color: enableDarkTheme
                  ? _palette.dark.textPrimary
                  : _palette.light.textPrimary,
                fontSize: '11px',
              },
            },
          }}
        >
          <MenuItem
            value="shiftHours"
            sx={{
              fontSize: '11px',
            }}
          >
            {t('utilization_by_shift_hours')}
          </MenuItem>
          <MenuItem
            value="engineHours"
            sx={{
              fontSize: '11px',
            }}
          >
            {t('utilization_by_engine_hours')}
          </MenuItem>
        </Select>
        <div
          style={{
            color: enableDarkTheme
              ? ThemePalette.typography.mediumGray
              : '#3d3d3d',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Typography style={{ fontSize: '11px' }}>
            {t('shift_wise_analysis')}
          </Typography>
          <StyledSwitchButton
            disableRipple
            checked={shiftWiseAnalysis}
            disabled={isLoadingFleetSummary}
            onChange={(e) => {
              setShiftWiseAnalysis(e.target.checked);
            }}
            color="secondary"
          />
        </div>
        {shiftWiseAnalysis && (
          <Select
            labelId="shift-number-label"
            value={shiftNumber}
            defaultValue={shiftNumber}
            disabled={isLoadingFleetSummary}
            onChange={(e) => {
              setShiftNumber(e.target.value);
            }}
            sx={{
              height: '30px',
              fontSize: '11px',
              marginLeft: '10px',
              marginRight: '20px',
              backgroundColor: enableDarkTheme
                ? palette.cartesianGrid
                : palette.menuOptions,
              color: enableDarkTheme
                ? _palette.dark.textPrimary
                : _palette.light.textPrimary,
              '& .MuiSelect-icon': {
                color: palette.primary,
              },
              '& fieldset': {
                borderColor: palette.borderGrey,
              },
              '&.MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: palette.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                },
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: enableDarkTheme
                    ? _palette.dark.menuOptions
                    : _palette.light.menuOptions,
                  color: enableDarkTheme
                    ? _palette.dark.textPrimary
                    : _palette.light.textPrimary,
                  fontSize: '11px',
                },
              },
            }}
          >
            {shiftOptions.map((shift, index) => {
              const todayLocal = dayjs().tz(timezone);
              const startTimeStr = formatShiftHour(shift.startHour, todayLocal);
              const endTimeStr = formatShiftHour(shift.endHour, todayLocal);
              const label = shift.shiftName ?? `Shift ${index + 1}`;

              return (
                <MenuItem
                  key={index}
                  value={index + 1}
                  sx={{
                    fontSize: '11px',
                  }}
                >
                  {`${label} ${startTimeStr} - ${endTimeStr}`}
                </MenuItem>
              );
            })}
          </Select>
        )}
        {rangeIsOverMonth && (
          <div
            style={{
              marginLeft: '10px',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                color: enableDarkTheme
                  ? ThemePalette.typography.mediumGray
                  : '#3d3d3d',
                marginTop: '7px',
                marginRight: '10px',
              }}
            >
              {t('aggregation')}
            </Typography>

            <ProductionSelector
              options={aggregationOptions}
              selected={aggregation}
              setSelected={(value: string) => {
                setAggregation(value);
                if (isSelected === 'Custom') {
                  let sD = dayjs(startDate).toDate();
                  let eD = dayjs(endDate).toDate();
                  if (value === 'Monthly') {
                    const today = dayjs();

                    const isCurrentMonth =
                      dayjs(endDate).month() === today.month() &&
                      dayjs(endDate).year() === today.year();

                    sD = dayjs(startDate).startOf('month').toDate();

                    eD = isCurrentMonth
                      ? today.toDate()
                      : dayjs(endDate).endOf('month').toDate();
                  }
                  handleFetchCustomOnAggregationChange({
                    aggregation: value,
                    startDate: sD,
                    endDate: eD,
                  });
                }
              }}
              disabled={isLoadingFleetSummary}
              minWidth="100px"
              getOptionLabel={aggregationLabel}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(DateRangeDiv);
